import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import Select from "react-select";
import dayjs from "dayjs";
import {
  MdClose,
  MdFlag,
  MdOutlineChatBubbleOutline,
  MdOutlineCall,
  MdOutlineOpenInNew,
  MdOutlineSearch,
  MdOutlineStarBorder,
  MdOutlineWarningAmber,
  MdUnfoldMore,
} from "react-icons/md";
import Sidebar from "../Sidebar";
import Layout from "../../layout/Layout";
import OrderDetailsModal from "../AdminBookings/OrderDetailsModal";
import { GET_BOOKINGS, UPDATE_ORDER_CALLING_STATUS } from "../AdminBookings/graphql";
import { GET_CUSTOMER_FEEDBACK_REPORT, GET_CUSTOMER_FEEDBACK_REVIEWS } from "./graphql";

const PAGE_SIZE = 20;
// Fewer reviews than this and an average says very little.
const FEW_REVIEWS = 5;

const PRESETS = [
  { key: "today", label: "Today" },
  { key: "last7", label: "7 days" },
  { key: "last30", label: "30 days" },
  { key: "last90", label: "90 days" },
  { key: "custom", label: "Custom" },
];

const fmt = (v, d = 1) => (v === null || v === undefined ? "—" : Number(v).toFixed(d));
const pct = (a, b) => (b ? `${Math.round((a / b) * 100)}%` : "—");
const plural = (n, one, many) => `${n} ${n === 1 ? one : many || `${one}s`}`;

// Colour a 1-5 average: green is good, amber needs a look, red needs action.
const tone = (v) => {
  if (v === null || v === undefined) return { text: "text-gray-400", bg: "bg-gray-300", soft: "bg-gray-100 text-gray-500" };
  if (v >= 4.3) return { text: "text-emerald-600", bg: "bg-emerald-500", soft: "bg-emerald-50 text-emerald-700" };
  if (v >= 3.5) return { text: "text-amber-600", bg: "bg-amber-400", soft: "bg-amber-50 text-amber-700" };
  return { text: "text-red-600", bg: "bg-red-500", soft: "bg-red-50 text-red-700" };
};

// One colour per star count, worst to best.
const STAR_BG = ["bg-red-500", "bg-orange-400", "bg-yellow-400", "bg-lime-500", "bg-emerald-500"];

const Stars = ({ value, size = "text-base" }) => (
  <span className={`${size} tracking-tight leading-none`} aria-label={`${value} out of 5`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <span key={n} className={n <= (value || 0) ? "text-amber-400" : "text-gray-300"}>★</span>
    ))}
  </span>
);

// Change against the previous period. For ratings, up is good; for counts
// like low ratings, pass goodWhenUp={false}. Tiny changes stay grey.
const Delta = ({ now, prev, digits = 1, goodWhenUp = true, suffix = "", title }) => {
  if (now === null || now === undefined || prev === null || prev === undefined) return null;
  const d = now - prev;
  const flat = Math.abs(d) < (digits ? 0.05 : 0.5);
  const good = goodWhenUp ? d > 0 : d < 0;
  const cls = flat ? "text-gray-400" : good ? "text-emerald-600" : "text-red-600";
  const text = flat ? "no change" : `${d > 0 ? "↑" : "↓"}${Math.abs(d).toFixed(digits)}${suffix}`;
  return <span className={`text-xs font-medium whitespace-nowrap ${cls}`} title={title}>{text}</span>;
};

const Card = ({ title, hint, right, children, className = "" }) => (
  <section className={`bg-white rounded-xl border border-gray-100 shadow-sm ${className}`}>
    {(title || right) && (
      <header className="flex flex-wrap items-start justify-between gap-2 px-4 pt-4">
        <div>
          <h2 className="font-semibold text-gray-900">{title}</h2>
          {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}
        </div>
        {right}
      </header>
    )}
    <div className="p-4">{children}</div>
  </section>
);

const Kpi = ({ label, value, sub, valueClass = "text-gray-900", onClick, active, delta }) => {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      onClick={onClick}
      className={`text-left bg-white rounded-xl border p-4 shadow-sm ${onClick ? "hover:border-amber-300 cursor-pointer" : ""} ${active ? "border-amber-400 ring-1 ring-amber-300" : "border-gray-100"}`}
    >
      <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
      <p className={`text-3xl font-semibold mt-1 ${valueClass}`}>
        {value}
        {delta && <span className="ml-2 align-middle">{delta}</span>}
      </p>
      {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
    </Tag>
  );
};

// Five rows, 5★ on top. Clicking a row filters the review list to that rating.
const Distribution = ({ counts, selected, onPick }) => {
  const total = counts.reduce((a, b) => a + b, 0);
  const max = Math.max(1, ...counts);
  return (
    <div className="space-y-1.5">
      {[5, 4, 3, 2, 1].map((n) => {
        const c = counts[n - 1] || 0;
        const on = selected.includes(n);
        return (
          <button
            key={n}
            type="button"
            disabled={!c}
            onClick={() => onPick(n)}
            className={`w-full flex items-center gap-2 text-sm rounded-md px-1 py-0.5 ${c ? "hover:bg-gray-50" : "opacity-50 cursor-default"} ${on ? "bg-amber-50 ring-1 ring-amber-300" : ""}`}
          >
            <span className="w-8 text-gray-600 text-left">{n} ★</span>
            <span className="flex-1 h-2.5 rounded-full bg-gray-100 overflow-hidden">
              <span className={`block h-full rounded-full ${STAR_BG[n - 1]}`} style={{ width: `${(c / max) * 100}%` }} />
            </span>
            <span className="w-16 text-right text-gray-700 tabular-nums">
              {c} <span className="text-gray-400 text-xs">{pct(c, total)}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
};

// The trend strip: bar height = number of reviews, colour = average service
// rating. One bar per day, week or month depending on the range.
const BUCKET = {
  DAY: { title: "Day by day", label: (d) => dayjs(d).format("D MMM") },
  WEEK: { title: "Week by week", label: (d) => `Week of ${dayjs(d).format("D MMM")}` },
  MONTH: { title: "Month by month", label: (d) => dayjs(d).format("MMM YYYY") },
};
const Trend = ({ points, bucket }) => {
  const b = BUCKET[bucket] || BUCKET.DAY;
  if (!points.length) return <p className="text-sm text-gray-400 py-6 text-center">No reviews in this period</p>;
  const max = Math.max(...points.map((p) => p.reviews));
  const short = (d) => (bucket === "MONTH" ? dayjs(d).format("MMM YY") : dayjs(d).format("D MMM"));
  return (
    <div>
      <div className="flex items-end gap-[3px] h-28">
        {points.map((p) => (
          <div
            key={p.date}
            className="flex-1 min-w-[4px] max-w-[28px] flex flex-col justify-end h-full"
            title={`${b.label(p.date)} · ${plural(p.reviews, "review")} · avg ${fmt(p.avgServiceRating)}★`}
          >
            <div className={`rounded-t ${tone(p.avgServiceRating).bg}`} style={{ height: `${Math.max(6, (p.reviews / max) * 100)}%` }} />
          </div>
        ))}
      </div>
      <div className="flex justify-between text-xs text-gray-400 mt-1">
        <span>{short(points[0].date)}</span>
        <span>{short(points[points.length - 1].date)}</span>
      </div>
      <p className="text-xs text-gray-400 mt-2">Taller = more reviews. Green = happy, red = unhappy. Hover a bar for details.</p>
    </div>
  );
};

const SORT_COLUMNS = [
  { key: "employeeName", label: "Beautician", align: "left" },
  { key: "reviews", label: "Reviews", align: "right" },
  { key: "coverage", label: "Reviewed", align: "right", hint: "Share of completed bookings that got a customer review" },
  { key: "avgServiceRating", label: "Service ★", align: "right", hint: "Plain average of the service ratings" },
  {
    key: "fairServiceScore",
    label: "Fair score",
    align: "right",
    hint: "The average, pulled toward the overall average when there are few reviews - so 2 reviews of 5★ don't rank above 40 reviews of 4.6★. Sort by this.",
  },
  { key: "serviceChange", label: "Change", align: "right", hint: "Service rating against the previous period of the same length" },
  { key: "mix", label: "1★ → 5★", align: "left", nosort: true },
  { key: "complaints", label: "Top complaints", align: "left", nosort: true, hint: "Most picked options by customers who gave 3★ or less" },
  { key: "avgProductRating", label: "Product ★", align: "right" },
  { key: "lowCount", label: "Low (1–2★)", align: "right" },
  { key: "flaggedCount", label: "Flagged", align: "right" },
  { key: "commentCount", label: "Comments", align: "right" },
  { key: "lastReviewedOn", label: "Last booking", align: "right" },
];

const EmployeeTable = ({ rows, selectedIds, onToggle }) => {
  const [sort, setSort] = useState({ key: "fairServiceScore", dir: "desc" });
  const value = (r, key) => {
    if (key === "coverage") return r.completedOrders ? r.reviewedCompleted / r.completedOrders : null;
    if (key === "employeeName") return (r.employeeName || "").toLowerCase();
    if (key === "serviceChange") {
      return r.avgServiceRating !== null && r.prevAvgServiceRating !== null ? r.avgServiceRating - r.prevAvgServiceRating : null;
    }
    return r[key];
  };
  const sorted = useMemo(() => {
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const x = value(a, sort.key);
      const y = value(b, sort.key);
      if (x === null || x === undefined) return 1;
      if (y === null || y === undefined) return -1;
      if (x < y) return -1 * dir;
      if (x > y) return 1 * dir;
      return 0;
    });
  }, [rows, sort]);

  const clickHeader = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: key === "employeeName" ? "asc" : "desc" }));

  if (!rows.length) return <p className="text-sm text-gray-400 py-6 text-center">No completed bookings in this period</p>;

  return (
    <div className="overflow-x-auto -mx-4">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-xs text-gray-500 border-b border-gray-100">
            {SORT_COLUMNS.map((c) => (
              <th
                key={c.key}
                title={c.hint}
                className={`px-3 py-2 font-medium whitespace-nowrap ${c.align === "right" ? "text-right" : "text-left"} ${c.nosort ? "" : "cursor-pointer select-none hover:text-gray-800"}`}
                onClick={c.nosort ? undefined : () => clickHeader(c.key)}
              >
                {c.label}
                {!c.nosort && (
                  <span className={`ml-0.5 align-middle ${sort.key === c.key ? "text-amber-600" : "text-gray-300"}`}>
                    {sort.key === c.key ? (sort.dir === "asc" ? "▲" : "▼") : <MdUnfoldMore className="inline" />}
                  </span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => {
            const on = selectedIds.includes(String(r.employeeId));
            const total = r.serviceCounts.reduce((a, b) => a + b, 0);
            const few = r.reviews > 0 && r.reviews < FEW_REVIEWS;
            const attention = r.reviews >= 3 && r.avgServiceRating !== null && r.avgServiceRating < 3.5;
            return (
              <tr
                key={r.employeeId}
                onClick={() => onToggle(String(r.employeeId))}
                className={`border-b border-gray-50 cursor-pointer ${on ? "bg-amber-50" : "hover:bg-gray-50"}`}
              >
                <td className="px-3 py-2.5 font-medium text-gray-900 whitespace-nowrap">
                  {r.employeeName}
                  {attention && <span className="ml-2 text-[11px] font-medium px-1.5 py-0.5 rounded bg-red-50 text-red-700">Needs attention</span>}
                  {few && <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">Few reviews</span>}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">{r.reviews}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-gray-600" title={`${r.reviewedCompleted} of ${r.completedOrders} completed bookings were reviewed`}>
                  {pct(r.reviewedCompleted, r.completedOrders)}
                  <span className="text-gray-400 text-xs"> ({r.reviewedCompleted}/{r.completedOrders})</span>
                </td>
                <td className={`px-3 py-2.5 text-right tabular-nums ${tone(r.avgServiceRating).text}`}>{fmt(r.avgServiceRating)}</td>
                <td className={`px-3 py-2.5 text-right tabular-nums font-semibold ${tone(r.fairServiceScore).text}`} title={r.serviceRated ? `${fmt(r.avgServiceRating)}★ from ${plural(r.serviceRated, "review")}` : undefined}>
                  {fmt(r.fairServiceScore)}
                </td>
                <td className="px-3 py-2.5 text-right">
                  {r.avgServiceRating !== null && r.prevAvgServiceRating !== null ? (
                    <span className={r.prevServiceRated < FEW_REVIEWS ? "opacity-60" : ""}>
                      <Delta
                        now={r.avgServiceRating}
                        prev={r.prevAvgServiceRating}
                        title={`Previous period: ${fmt(r.prevAvgServiceRating)}★ from ${plural(r.prevServiceRated, "review")}`}
                      />
                    </span>
                  ) : (
                    <span className="text-xs text-gray-300" title="No reviews in one of the two periods">—</span>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  {total ? (
                    <div className="flex h-2.5 w-28 rounded-full overflow-hidden bg-gray-100" title={r.serviceCounts.map((c, i) => `${i + 1}★: ${c}`).join("  ")}>
                      {r.serviceCounts.map((c, i) => (c ? <span key={i} className={STAR_BG[i]} style={{ width: `${(c / total) * 100}%` }} /> : null))}
                    </div>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  {r.topComplaints.length ? (
                    <div className="flex flex-wrap gap-1 max-w-[220px]">
                      {r.topComplaints.map((c) => (
                        <span key={c.option} className="text-[11px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 whitespace-nowrap">
                          {c.option} ×{c.count}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-xs text-gray-300">—</span>
                  )}
                </td>
                <td className={`px-3 py-2.5 text-right tabular-nums ${tone(r.avgProductRating).text}`}>{fmt(r.avgProductRating)}</td>
                <td className={`px-3 py-2.5 text-right tabular-nums ${r.lowCount ? "text-red-600 font-medium" : "text-gray-400"}`}>{r.lowCount}</td>
                <td className={`px-3 py-2.5 text-right tabular-nums ${r.flaggedCount ? "text-red-600 font-medium" : "text-gray-400"}`}>{r.flaggedCount}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-gray-600">{r.commentCount}</td>
                <td className="px-3 py-2.5 text-right text-gray-500 whitespace-nowrap">{r.lastReviewedOn ? dayjs(r.lastReviewedOn).format("D MMM") : "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const GROUPS = [
  { key: "SERVICE", title: "About the service", types: ["SERVICE", "EMPLOYEE"] },
  { key: "PRODUCT", title: "About the products used", types: ["PRODUCT"] },
];

// What the customers picked in the follow-up questions. The colour is the
// average star rating of the reviews that picked it, so complaints stand out.
const Reasons = ({ reasons }) => {
  const [showAll, setShowAll] = useState({});
  if (!reasons.length) return <p className="text-sm text-gray-400 py-6 text-center">No follow-up answers in this period</p>;
  return (
    <div className="grid md:grid-cols-2 gap-6">
      {GROUPS.map((g) => {
        const byQuestion = {};
        reasons.filter((r) => g.types.includes(r.targetType)).forEach((r) => {
          (byQuestion[r.question] = byQuestion[r.question] || []).push(r);
        });
        const questions = Object.entries(byQuestion);
        return (
          <div key={g.key}>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">{g.title}</h3>
            {!questions.length && <p className="text-sm text-gray-400">No answers</p>}
            {questions.map(([q, items]) => {
              const id = `${g.key}:${q}`;
              const max = Math.max(...items.map((i) => i.count));
              const shown = showAll[id] ? items : items.slice(0, 6);
              return (
                <div key={q} className="mb-4">
                  <p className="text-xs text-gray-500 mb-1.5">{q}</p>
                  <div className="space-y-1">
                    {shown.map((i) => (
                      <div key={i.option} className="flex items-center gap-2 text-sm">
                        <span className="w-40 sm:w-48 truncate text-gray-800" title={i.option}>{i.option}</span>
                        <span className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                          <span className={`block h-full rounded-full ${tone(i.avgRating).bg}`} style={{ width: `${(i.count / max) * 100}%` }} />
                        </span>
                        <span className="w-8 text-right tabular-nums text-gray-700">{i.count}</span>
                        <span className={`w-10 text-right text-xs tabular-nums ${tone(i.avgRating).text}`} title="Average star rating of customers who picked this">
                          {fmt(i.avgRating)}★
                        </span>
                      </div>
                    ))}
                  </div>
                  {items.length > 6 && (
                    <button type="button" className="text-xs text-amber-700 mt-1" onClick={() => setShowAll((s) => ({ ...s, [id]: !s[id] }))}>
                      {showAll[id] ? "Show fewer" : `Show all ${items.length}`}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

// Did unhappy customers book again, split by whether someone called them.
const Recovery = ({ r }) => {
  const rows = [
    { label: "Flagged, we spoke to them (booked after the call)", n: r.called, k: r.calledRebooked, bar: "bg-emerald-500" },
    { label: "Flagged, not reached yet", n: r.notCalled, k: r.notCalledRebooked, bar: "bg-red-500" },
    { label: "Everyone else who reviewed", n: r.others, k: r.othersRebooked, bar: "bg-gray-400", baseline: true },
  ];
  return (
    <Card
      title="Did unhappy customers come back?"
      hint="Share who booked again (on a later day, not cancelled) after the reviewed booking."
    >
      <div className="space-y-3">
        {rows.map((x) => (
          <div key={x.label} className="flex items-center gap-3 text-sm">
            <span className={`w-72 shrink-0 ${x.baseline ? "text-gray-500" : "text-gray-800"}`}>{x.label}</span>
            <span className="flex-1 h-3 rounded-full bg-gray-100 overflow-hidden">
              <span className={`block h-full rounded-full ${x.bar}`} style={{ width: x.n ? `${(x.k / x.n) * 100}%` : 0 }} />
            </span>
            <span className="w-32 text-right tabular-nums">
              <span className="font-semibold text-gray-900">{pct(x.k, x.n)}</span>
              <span className="text-gray-400 text-xs"> ({x.k}/{x.n})</span>
            </span>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-1 mt-4 text-sm text-gray-700">
        <span>
          Typical time from review to call:{" "}
          <strong>{r.medianHoursToCall === null ? "—" : r.medianHoursToCall < 1 ? "under 1 h" : r.medianHoursToCall < 24 ? `${Math.round(r.medianHoursToCall)} h` : `${(r.medianHoursToCall / 24).toFixed(1)} days`}</strong>
          <span className="text-gray-400 text-xs"> (median of {plural(r.timedCalls, "logged call")})</span>
        </span>
        {r.calledUntimed > 0 && (
          <span className="text-xs text-gray-400 self-center">{plural(r.calledUntimed, "call")} logged before call times were kept count from the booking date.</span>
        )}
      </div>
      <p className="text-xs text-gray-400 mt-3">
        Bookings near the end of the period have had less time to rebook, so recent numbers run low. Small groups swing a lot. Treat them as a signal, not proof.
      </p>
    </Card>
  );
};

const Chip = ({ children, low }) => (
  <span className={`text-xs px-2 py-0.5 rounded-full ${low ? "bg-red-50 text-red-700" : "bg-gray-100 text-gray-700"}`}>{children}</span>
);

const AspectBlock = ({ title, rating, comment, answers, na }) => (
  <div className="min-w-0">
    <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">{title}</p>
    {rating ? (
      <>
        <div className="flex items-center gap-2">
          <Stars value={rating} />
          <span className={`text-sm font-semibold ${tone(rating).text}`}>{rating}/5</span>
        </div>
        {answers.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {answers.flatMap((a) => a.options.map((o) => <Chip key={`${a.question}:${o}`} low={rating <= 3}>{o}</Chip>))}
          </div>
        )}
        {answers.filter((a) => a.text).map((a) => (
          <p key={a.question} className="text-sm text-gray-700 mt-2"><span className="text-gray-400">{a.question}: </span>{a.text}</p>
        ))}
        {comment ? (
          <p className="text-sm text-gray-800 mt-2 border-l-2 border-gray-200 pl-3 whitespace-pre-line break-words">{comment}</p>
        ) : (
          <p className="text-xs text-gray-400 mt-2">No written comment</p>
        )}
      </>
    ) : (
      <p className="text-sm text-gray-400">{na}</p>
    )}
  </div>
);

const CALL_STATUSES = [
  { value: "PENDING", label: "Not called yet" },
  { value: "COMPLETED", label: "Called – spoke to customer" },
  { value: "NO_ANSWER", label: "Called – no answer" },
  { value: "NOT_REACHABLE", label: "Not reachable" },
];
const ISSUE_STATUSES = [
  { value: "NO_ISSUE", label: "No issue" },
  { value: "NEEDS_RESOLUTION", label: "Issue – needs resolution" },
  { value: "RESOLVED", label: "Issue resolved" },
];
const callLabel = (v) => (CALL_STATUSES.find((c) => c.value === v) || CALL_STATUSES[0]).label;

const followUpState = (r) => {
  if (r.feedbackIssueStatus === "NEEDS_RESOLUTION") return { text: "Issue open", cls: "bg-red-50 text-red-700" };
  if (r.feedbackCallStatus === "COMPLETED") return { text: r.feedbackIssueStatus === "RESOLVED" ? "Resolved" : "Called", cls: "bg-emerald-50 text-emerald-700" };
  if (r.feedbackCallStatus === "PENDING") return { text: "Call pending", cls: "bg-red-50 text-red-700" };
  return { text: callLabel(r.feedbackCallStatus).replace("Called – ", ""), cls: "bg-amber-50 text-amber-700" };
};

const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:border-amber-400";

// Admin's call-back on a flagged review. Uses the same booking fields as the
// "Customer Post-Service Feedback" block in the booking form, so both places
// always agree.
const FollowUp = ({ r, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [save, { loading }] = useMutation(UPDATE_ORDER_CALLING_STATUS);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    feedbackCallStatus: r.feedbackCallStatus,
    feedbackCallComment: r.feedbackCallComment || "",
    feedbackIssueStatus: r.feedbackIssueStatus,
    feedbackIssueDetails: r.feedbackIssueDetails || "",
    feedbackResolutionDetails: r.feedbackResolutionDetails || "",
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const state = followUpState(r);
  const phone = (r.customerPhone || "").replace(/\s+/g, "");

  const submit = async () => {
    setError(null);
    if (form.feedbackCallStatus === "COMPLETED" && !form.feedbackCallComment.trim()) {
      setError("Write what the customer told you.");
      return;
    }
    if (form.feedbackIssueStatus !== "NO_ISSUE" && !form.feedbackIssueDetails.trim()) {
      setError("Describe the issue.");
      return;
    }
    if (form.feedbackIssueStatus === "RESOLVED" && !form.feedbackResolutionDetails.trim()) {
      setError("Write how it was resolved.");
      return;
    }
    const data = {
      orderId: r.orderId,
      ...form,
      feedbackIssueDetails: form.feedbackIssueStatus === "NO_ISSUE" ? "" : form.feedbackIssueDetails,
      feedbackResolutionDetails: form.feedbackIssueStatus === "RESOLVED" ? form.feedbackResolutionDetails : "",
    };
    try {
      const res = await save({ variables: { data } });
      const out = res.data?.updateOrderCallingStatus;
      if (out?.status !== "SUCCESS") throw new Error(out?.message || "Could not save");
      // Same rule as the server: a changed outcome, or new notes with a called
      // outcome, is a call - stamp it here too so the card is right at once.
      const called = data.feedbackCallStatus !== "PENDING"
        && (data.feedbackCallStatus !== r.feedbackCallStatus || (data.feedbackCallComment || "") !== (r.feedbackCallComment || ""));
      onSaved(r.feedbackId, called
        ? { ...data, feedbackCallAt: new Date().toISOString(), feedbackCallBy: "you", feedbackCallAttempts: (r.feedbackCallAttempts || 0) + 1 }
        : data);
      setOpen(false);
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div className={`mt-3 rounded-lg border px-3 py-2.5 ${r.isFlagged && r.feedbackCallStatus !== "COMPLETED" ? "border-red-200 bg-red-50/40" : "border-gray-100 bg-gray-50"}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase tracking-wide text-gray-500">Follow-up</span>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${state.cls}`}>{state.text}</span>
        {phone && (
          <a href={`tel:${phone}`} className="inline-flex items-center gap-1 text-sm text-blue-700 hover:underline">
            <MdOutlineCall /> Call {r.customerPhone}
          </a>
        )}
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="ml-auto text-sm px-3 py-1 rounded-md border border-gray-200 bg-white hover:border-amber-400"
        >
          {open ? "Cancel" : r.feedbackCallStatus === "PENDING" ? "Log call" : "Update"}
        </button>
      </div>

      {r.feedbackCallAt && (
        <p className="mt-1.5 text-xs text-gray-500">
          Last call {dayjs(r.feedbackCallAt).format("D MMM, h:mm A")} by {r.feedbackCallBy || "Admin"}
          {r.feedbackCallAttempts > 1 ? ` · ${r.feedbackCallAttempts} tries` : ""}
        </p>
      )}
      {!open && (r.feedbackCallComment || r.feedbackIssueDetails || r.feedbackResolutionDetails) && (
        <div className="mt-2 text-sm text-gray-700 space-y-1">
          {r.feedbackCallComment && <p><span className="text-gray-400">Call notes: </span>{r.feedbackCallComment}</p>}
          {r.feedbackIssueDetails && <p><span className="text-gray-400">Issue: </span>{r.feedbackIssueDetails}</p>}
          {r.feedbackResolutionDetails && (
            <p><span className="text-gray-400">Resolution: </span>{r.feedbackResolutionDetails}{r.feedbackResolvedAt ? ` (${dayjs(r.feedbackResolvedAt).format("D MMM")})` : ""}</p>
          )}
        </div>
      )}

      {open && (
        <div className="mt-3 grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-500">Call outcome</label>
            <select value={form.feedbackCallStatus} onChange={set("feedbackCallStatus")} className={inputCls}>
              {CALL_STATUSES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500">Issue</label>
            <select value={form.feedbackIssueStatus} onChange={set("feedbackIssueStatus")} className={inputCls}>
              {ISSUE_STATUSES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs text-gray-500">What the customer said (call notes)</label>
            <textarea value={form.feedbackCallComment} onChange={set("feedbackCallComment")} maxLength={250} rows={2} className={inputCls} placeholder="e.g. Beautician came late, wants a free re-do" />
          </div>
          {form.feedbackIssueStatus !== "NO_ISSUE" && (
            <div className="sm:col-span-2">
              <label className="text-xs text-gray-500">Issue details</label>
              <textarea value={form.feedbackIssueDetails} onChange={set("feedbackIssueDetails")} maxLength={250} rows={2} className={inputCls} />
            </div>
          )}
          {form.feedbackIssueStatus === "RESOLVED" && (
            <div className="sm:col-span-2">
              <label className="text-xs text-gray-500">How it was resolved</label>
              <textarea value={form.feedbackResolutionDetails} onChange={set("feedbackResolutionDetails")} maxLength={250} rows={2} className={inputCls} />
            </div>
          )}
          {error && <p className="sm:col-span-2 text-sm text-red-600">{error}</p>}
          <div className="sm:col-span-2 flex justify-end">
            <button type="button" disabled={loading} onClick={submit} className="px-4 py-1.5 rounded-md bg-[#FBB033] text-black text-sm font-semibold hover:brightness-95 disabled:opacity-60">
              {loading ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const ReviewCard = ({ r, onOpenBooking, onFollowUpSaved, opening }) => {
  const serviceAnswers = r.answers.filter((a) => a.targetType !== "PRODUCT");
  const productAnswers = r.answers.filter((a) => a.targetType === "PRODUCT");
  const low = (r.serviceRating && r.serviceRating <= 2) || (r.productRating && r.productRating <= 2);
  const needsFollowUp = r.isFlagged || r.feedbackIssueStatus !== "NO_ISSUE";
  return (
    <article className={`bg-white rounded-xl border shadow-sm p-4 ${r.isFlagged && r.feedbackCallStatus !== "COMPLETED" ? "border-red-200" : "border-gray-100"}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-semibold text-gray-900">{r.employeeName || "Unknown beautician"}</p>
          <p className="text-xs text-gray-500 mt-0.5">{r.services.length ? r.services.join(" · ") : "Services not recorded"}</p>
        </div>
        <div className="text-right text-xs text-gray-500">
          <p>
            {r.bookingDate ? dayjs(r.bookingDate).format("D MMM YYYY") : "—"}
            {r.bookingId && (
              <>
                {" · "}
                <button
                  type="button"
                  onClick={() => onOpenBooking(r)}
                  disabled={opening}
                  title="Open booking details"
                  className="inline-flex items-center gap-0.5 text-blue-700 hover:underline disabled:opacity-60"
                >
                  {r.bookingId} <MdOutlineOpenInNew />
                </button>
              </>
            )}
          </p>
          <div className="flex justify-end gap-1.5 mt-1">
            {r.isFlagged && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-red-50 text-red-700 font-medium"><MdFlag /> Flagged</span>
            )}
            {r.reviewPublic && <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">OK to show publicly</span>}
            {r.nextBookingDate && (
              <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700" title="This customer booked again afterwards">
                Booked again · {dayjs(r.nextBookingDate).format("D MMM")}
              </span>
            )}
            {!r.nextBookingDate && r.isFlagged && <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">Not booked again yet</span>}
            {r.isLegacy && <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-500" title="Rated in the old app: stars and comment only">Old app</span>}
          </div>
        </div>
      </div>
      <div className={`grid sm:grid-cols-2 gap-4 mt-3 pt-3 border-t ${low ? "border-red-100" : "border-gray-100"}`}>
        <AspectBlock title="Service" rating={r.serviceRating} comment={r.serviceComment} answers={serviceAnswers} na="Not rated" />
        <AspectBlock title="Products used" rating={r.productRating} comment={r.productComment} answers={productAnswers} na={r.isLegacy ? "Not asked in the old app" : "Not rated"} />
      </div>
      <p className="text-xs text-gray-500 mt-3">
        {r.customerName || "Customer"}
        {r.customerPhone ? ` · ${r.customerPhone}` : ""}
      </p>
      {needsFollowUp && <FollowUp key={`${r.feedbackId}:${r.feedbackCallStatus}:${r.feedbackIssueStatus}`} r={r} onSaved={onFollowUpSaved} />}
    </article>
  );
};

const selectStyles = {
  control: (b) => ({ ...b, minHeight: 38, borderColor: "#e5e7eb", boxShadow: "none" }),
  multiValue: (b) => ({ ...b, backgroundColor: "#FBB033" }),
};

const emptyList = { serviceRatings: [], productRatings: [], onlyFlagged: false, onlyWithComment: false, onlyLow: false, followUp: "", search: "", sort: "NEWEST" };

const CustomerFeedback = () => {
  const client = useApolloClient();
  const reviewsRef = useRef(null);

  const [preset, setPreset] = useState("last30");
  const [custom, setCustom] = useState({
    start: dayjs().subtract(29, "day").format("YYYY-MM-DD"),
    end: dayjs().format("YYYY-MM-DD"),
  });
  const [employeeIds, setEmployeeIds] = useState([]);
  const [list, setList] = useState(emptyList);
  const [searchText, setSearchText] = useState("");
  const [fetchBooking, { loading: openingBooking }] = useMutation(GET_BOOKINGS);
  const [viewingBooking, setViewingBooking] = useState(null);

  const range = useMemo(() => {
    const today = dayjs().format("YYYY-MM-DD");
    if (preset === "today") return { startDate: today, endDate: today };
    if (preset === "last7") return { startDate: dayjs().subtract(6, "day").format("YYYY-MM-DD"), endDate: today };
    if (preset === "last90") return { startDate: dayjs().subtract(89, "day").format("YYYY-MM-DD"), endDate: today };
    if (preset === "custom") return { startDate: custom.start, endDate: custom.end };
    return { startDate: dayjs().subtract(29, "day").format("YYYY-MM-DD"), endDate: today };
  }, [preset, custom]);

  // Everyone in the period (feeds the beautician table), and the same period
  // narrowed to the chosen beauticians (feeds the cards, charts and reviews).
  const allQ = useQuery(GET_CUSTOMER_FEEDBACK_REPORT, { variables: { filter: range }, fetchPolicy: "cache-and-network" });
  const selQ = useQuery(GET_CUSTOMER_FEEDBACK_REPORT, {
    variables: { filter: { ...range, employeeIds } },
    skip: employeeIds.length === 0,
    fetchPolicy: "cache-and-network",
  });

  const allRes = allQ.data?.getCustomerFeedbackReport;
  const activeQ = employeeIds.length ? selQ : allQ;
  const activeRes = activeQ.data?.getCustomerFeedbackReport;
  const all = allRes?.data;
  const report = activeRes?.data;
  const summary = report?.summary;
  const errorMessage = allQ.error?.message || selQ.error?.message || (allRes?.status === "ERROR" && allRes.message) || (activeRes?.status === "ERROR" && activeRes.message);

  const employeeOptions = useMemo(
    () => (all?.employees || []).map((e) => ({ value: String(e.employeeId), label: e.employeeName })).sort((a, b) => a.label.localeCompare(b.label)),
    [all]
  );
  const selectedNames = employeeOptions.filter((o) => employeeIds.includes(o.value)).map((o) => o.label);

  // Debounce the search box.
  useEffect(() => {
    const t = setTimeout(() => setList((l) => (l.search === searchText.trim() ? l : { ...l, search: searchText.trim() })), 400);
    return () => clearTimeout(t);
  }, [searchText]);

  // Review list: reloads from the top whenever a filter changes.
  const [reviews, setReviews] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState(null);
  const requestId = useRef(0);
  const listKey = JSON.stringify([range, employeeIds, list]);

  const loadPage = useCallback(
    async (offset) => {
      const id = ++requestId.current;
      setLoadingList(true);
      setListError(null);
      try {
        const { data } = await client.query({
          query: GET_CUSTOMER_FEEDBACK_REVIEWS,
          variables: { filter: { ...range, employeeIds, ...list }, limit: PAGE_SIZE, offset },
          fetchPolicy: "network-only",
        });
        if (id !== requestId.current) return;
        const res = data.getCustomerFeedbackReviews;
        if (res.status !== "SUCCESS") throw new Error(res.message || "Could not load reviews");
        setTotal(res.data.total);
        setReviews((prev) => (offset ? [...prev, ...res.data.reviews] : res.data.reviews));
      } catch (e) {
        if (id === requestId.current) setListError(e.message);
      } finally {
        if (id === requestId.current) setLoadingList(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listKey, client]
  );
  useEffect(() => {
    loadPage(0);
  }, [loadPage]);

  const openBooking = async (r) => {
    try {
      const res = await fetchBooking({ variables: { data: { id: r.orderId, bookingId: r.bookingId } } });
      const orders = res.data?.getBookings?.orders || [];
      const order = orders.find((o) => String(o.id) === String(r.orderId));
      if (!order) throw new Error(res.data?.getBookings?.message || "Booking not found");
      setViewingBooking(order);
    } catch (e) {
      setListError(`Could not open the booking: ${e.message}`);
    }
  };

  // The follow-up was saved on the order: mirror it on the card and refresh the counts.
  const onFollowUpSaved = (feedbackId, saved) => {
    const { orderId, ...fields } = saved;
    setReviews((prev) =>
      prev.map((x) =>
        x.feedbackId === feedbackId
          ? { ...x, ...fields, feedbackResolvedAt: fields.feedbackIssueStatus === "RESOLVED" ? new Date().toISOString() : null }
          : x
      )
    );
    allQ.refetch();
    if (employeeIds.length) selQ.refetch();
  };

  const patchList = (patch) => setList((l) => ({ ...l, ...patch }));
  const toggleIn = (arr, n) => (arr.includes(n) ? arr.filter((x) => x !== n) : [...arr, n]);
  const scrollToReviews = () => setTimeout(() => reviewsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  const pickRating = (field) => (n) => {
    patchList({ [field]: toggleIn(list[field], n) });
    scrollToReviews();
  };
  const showLow = (field) => {
    setList({ ...emptyList, search: list.search, [field]: [1, 2] });
    scrollToReviews();
  };
  const toggleEmployee = (id) => setEmployeeIds((cur) => (cur.length === 1 && cur[0] === id ? [] : [id]));
  const clearList = () => {
    setList(emptyList);
    setSearchText("");
  };
  const listActive =
    list.serviceRatings.length || list.productRatings.length || list.onlyFlagged || list.onlyWithComment || list.onlyLow || list.followUp || list.search;

  const loadingFirst = (allQ.loading && !allQ.data) || (employeeIds.length > 0 && selQ.loading && !selQ.data);
  const prev = report?.previous;
  const prevLabel = prev ? `${dayjs(prev.startDate).format("D MMM")} – ${dayjs(prev.endDate).format("D MMM")}` : "";
  const lowService = summary ? summary.serviceCounts[0] + summary.serviceCounts[1] : 0;
  const lowProduct = summary ? summary.productCounts[0] + summary.productCounts[1] : 0;

  return (
    <div className="min-h-screen flex">
      <Sidebar />
      <Layout>
        <div className="max-w-7xl space-y-5 pb-16">
          <div>
            <h1 className="text-2xl font-semibold text-white">Customer feedback</h1>
            <p className="text-sm text-gray-400 mt-0.5">
              What customers said about each beautician's service and the products used, by booking date (IST).
            </p>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3 flex flex-wrap items-center gap-3">
            <div className="flex rounded-lg bg-gray-100 p-0.5">
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setPreset(p.key)}
                  className={`px-3 py-1.5 rounded-md text-sm ${preset === p.key ? "bg-white shadow-sm font-semibold text-gray-900" : "text-gray-600 hover:text-gray-900"}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {preset === "custom" && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="date"
                  value={custom.start}
                  max={custom.end}
                  onChange={(e) => e.target.value && setCustom((c) => ({ ...c, start: e.target.value }))}
                  className="border border-gray-200 rounded-lg px-2 py-1.5"
                />
                to
                <input
                  type="date"
                  value={custom.end}
                  min={custom.start}
                  onChange={(e) => e.target.value && setCustom((c) => ({ ...c, end: e.target.value }))}
                  className="border border-gray-200 rounded-lg px-2 py-1.5"
                />
              </div>
            )}
            <div className="min-w-[240px] flex-1 max-w-md">
              <Select
                isMulti
                placeholder="All beauticians"
                options={employeeOptions}
                value={employeeOptions.filter((o) => employeeIds.includes(o.value))}
                onChange={(sel) => setEmployeeIds(sel ? sel.map((s) => s.value) : [])}
                styles={selectStyles}
              />
            </div>
            <span className="text-xs text-gray-400 ml-auto">
              {dayjs(range.startDate).format("D MMM YYYY")} – {dayjs(range.endDate).format("D MMM YYYY")}
            </span>
          </div>

          {errorMessage && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">Could not load feedback: {errorMessage}</div>
          )}
          {loadingFirst && <div className="py-16 text-center text-gray-400">Loading…</div>}

          {!loadingFirst && summary && (
            <>
              {employeeIds.length > 0 && (
                <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 text-sm text-amber-900">
                  <span>Showing feedback for <strong>{selectedNames.join(", ") || "selected beauticians"}</strong> only</span>
                  <button type="button" onClick={() => setEmployeeIds([])} className="inline-flex items-center gap-1 font-medium hover:underline">
                    <MdClose /> Show everyone
                  </button>
                </div>
              )}

              {summary.totalReviews === 0 && (
                <div className="bg-white rounded-xl border border-dashed border-gray-300 py-10 px-6 text-center">
                  <MdOutlineStarBorder className="mx-auto text-4xl text-gray-300" />
                  <h3 className="mt-2 font-semibold text-gray-900">No customer reviews in this period</h3>
                  <p className="text-sm text-gray-500 mt-1">Try a longer date range, or clear the beautician filter.</p>
                </div>
              )}

              {summary.legacyCount > 0 && (
                <p className="text-xs text-gray-400 -mt-2">
                  Includes {plural(summary.legacyCount, "review")} from the old app: stars and comment only, no product rating or follow-up answers.
                </p>
              )}

              {prev && (
                <p className="text-xs text-gray-400 -mt-2">
                  Arrows compare with the {plural(dayjs(prev.endDate).diff(dayjs(prev.startDate), "day") + 1, "day")} before ({prevLabel}).
                </p>
              )}

              {/* Headline numbers */}
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                <Kpi
                  label="Service rating"
                  value={fmt(summary.avgServiceRating)}
                  valueClass={tone(summary.avgServiceRating).text}
                  delta={prev && <Delta now={summary.avgServiceRating} prev={prev.avgServiceRating} title={`${prevLabel}: ${fmt(prev.avgServiceRating)}`} />}
                  sub={`${plural(summary.serviceRated, "rating")} · beautician's service`}
                />
                <Kpi
                  label="Product rating"
                  value={fmt(summary.avgProductRating)}
                  valueClass={tone(summary.avgProductRating).text}
                  delta={prev && <Delta now={summary.avgProductRating} prev={prev.avgProductRating} title={`${prevLabel}: ${fmt(prev.avgProductRating)}`} />}
                  sub={`${plural(summary.productRated, "rating")} · products used`}
                />
                <Kpi
                  label="Reviews"
                  value={summary.totalReviews}
                  delta={prev && <Delta now={summary.totalReviews} prev={prev.totalReviews} digits={0} title={`${prevLabel}: ${prev.totalReviews}`} />}
                  sub={`${pct(summary.reviewedCompleted, summary.completedOrders)} of ${summary.completedOrders} completed bookings reviewed`}
                />
                <Kpi
                  label="Low ratings"
                  value={summary.lowReviews}
                  delta={prev && <Delta now={summary.lowReviews} prev={prev.lowReviews} digits={0} goodWhenUp={false} title={`${prevLabel}: ${prev.lowReviews}`} />}
                  valueClass={summary.lowReviews ? "text-red-600" : "text-gray-900"}
                  sub={`${pct(summary.lowReviews, summary.totalReviews)} of reviews · ${lowService} service, ${lowProduct} product (1–2★)`}
                  onClick={summary.lowReviews ? () => { setList({ ...emptyList, search: list.search, onlyLow: true }); scrollToReviews(); } : undefined}
                  active={list.onlyLow}
                />
                <Kpi
                  label="Flagged"
                  value={summary.flaggedCount}
                  valueClass={summary.flaggedCount ? "text-red-600" : "text-gray-900"}
                  sub={`Pending: ${summary.flaggedToCall} to call back · ${summary.openIssues} issue${summary.openIssues === 1 ? "" : "s"} open`}
                  onClick={summary.flaggedCount ? () => { setList({ ...emptyList, search: list.search, onlyFlagged: true }); scrollToReviews(); } : undefined}
                  active={list.onlyFlagged}
                />
              </div>

              {summary.flaggedToCall > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-800">
                  <span className="inline-flex items-center gap-2">
                    <MdOutlineCall className="text-lg" />
                    <span>
                      <strong>{plural(summary.flaggedToCall, "flagged review")}</strong> from the last 30 days still need a call to the customer{summary.openIssues ? `, and ${plural(summary.openIssues, "issue")} ${summary.openIssues === 1 ? "is" : "are"} still open` : ""}.
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={() => { setList({ ...emptyList, search: list.search, followUp: "TO_CALL" }); scrollToReviews(); }}
                    className="px-3 py-1 rounded-md bg-red-600 text-white font-medium hover:bg-red-700"
                  >
                    Show them
                  </button>
                </div>
              )}

              {/* Distribution + trend */}
              <div className="grid lg:grid-cols-3 gap-4">
                <Card title="Service ratings" hint="Click a row to see those reviews">
                  <Distribution counts={summary.serviceCounts} selected={list.serviceRatings} onPick={pickRating("serviceRatings")} />
                </Card>
                <Card title="Product ratings" hint="Click a row to see those reviews">
                  <Distribution counts={summary.productCounts} selected={list.productRatings} onPick={pickRating("productRatings")} />
                </Card>
                <Card title={(BUCKET[report.trendBucket] || BUCKET.DAY).title} hint="By booking date">
                  <Trend points={report.trend} bucket={report.trendBucket} />
                </Card>
              </div>
            </>
          )}

          {/* Beautician-wise */}
          {!loadingFirst && all && (
            <Card
              title="Beautician-wise"
              hint="Click a beautician to see only their feedback below. Click a column heading to sort."
              right={<span className="text-xs text-gray-400">{plural(all.employees.length, "beautician")}</span>}
            >
              <EmployeeTable rows={all.employees} selectedIds={employeeIds} onToggle={toggleEmployee} />
              <p className="text-xs text-gray-400 mt-3">
                Sorted by fair score: the average with 8 extra reviews at the overall average mixed in, so a handful of reviews can't top the list. "Needs attention" = 3+ reviews and average service rating under 3.5. "Reviewed" = completed bookings that got a customer review. Test bookings on dummy professionals are left out.
              </p>
            </Card>
          )}

          {!loadingFirst && report && (
            <Card title="Why customers gave these ratings" hint="Their answers to the follow-up questions. Bar colour = the average star rating of customers who picked it.">
              <Reasons reasons={report.reasons} />
            </Card>
          )}

          {!loadingFirst && report && report.recovery.flagged > 0 && <Recovery r={report.recovery} />}

          {/* Review list */}
          <div ref={reviewsRef} className="scroll-mt-4">
            <Card
              title="Individual reviews"
              hint={`${plural(total, "review")}${listActive ? " match your filters" : ""}`}
              right={
                listActive ? (
                  <button type="button" onClick={clearList} className="inline-flex items-center gap-1 text-sm text-amber-700 hover:underline">
                    <MdClose /> Clear filters
                  </button>
                ) : null
              }
            >
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <div className="relative">
                  <MdOutlineSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    placeholder="Search customer, booking, comment…"
                    className="border border-gray-200 rounded-lg pl-8 pr-3 py-1.5 text-sm w-64"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => patchList({ serviceRatings: list.serviceRatings.join() === "1,2" ? [] : [1, 2] })}
                  className={`text-sm px-3 py-1.5 rounded-full border ${list.serviceRatings.join() === "1,2" ? "bg-red-50 border-red-300 text-red-700" : "border-gray-200 text-gray-700 hover:border-gray-300"}`}
                >
                  Low service
                </button>
                <button
                  type="button"
                  onClick={() => patchList({ productRatings: list.productRatings.join() === "1,2" ? [] : [1, 2] })}
                  className={`text-sm px-3 py-1.5 rounded-full border ${list.productRatings.join() === "1,2" ? "bg-red-50 border-red-300 text-red-700" : "border-gray-200 text-gray-700 hover:border-gray-300"}`}
                >
                  Low product
                </button>
                <button
                  type="button"
                  onClick={() => patchList({ onlyFlagged: !list.onlyFlagged })}
                  className={`inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-full border ${list.onlyFlagged ? "bg-red-50 border-red-300 text-red-700" : "border-gray-200 text-gray-700 hover:border-gray-300"}`}
                >
                  <MdOutlineWarningAmber /> Flagged
                </button>
                <button
                  type="button"
                  onClick={() => patchList({ followUp: list.followUp === "TO_CALL" ? "" : "TO_CALL" })}
                  className={`inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-full border ${list.followUp === "TO_CALL" ? "bg-red-50 border-red-300 text-red-700" : "border-gray-200 text-gray-700 hover:border-gray-300"}`}
                >
                  <MdOutlineCall /> Needs call-back
                </button>
                <button
                  type="button"
                  onClick={() => patchList({ followUp: list.followUp === "OPEN_ISSUE" ? "" : "OPEN_ISSUE" })}
                  className={`text-sm px-3 py-1.5 rounded-full border ${list.followUp === "OPEN_ISSUE" ? "bg-red-50 border-red-300 text-red-700" : "border-gray-200 text-gray-700 hover:border-gray-300"}`}
                >
                  Open issues
                </button>
                <button
                  type="button"
                  onClick={() => patchList({ onlyWithComment: !list.onlyWithComment })}
                  className={`inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-full border ${list.onlyWithComment ? "bg-amber-50 border-amber-300 text-amber-800" : "border-gray-200 text-gray-700 hover:border-gray-300"}`}
                >
                  <MdOutlineChatBubbleOutline /> Has comment
                </button>
                <div className="flex items-center gap-0.5 ml-1" title="Show only these service ratings">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => patchList({ serviceRatings: toggleIn(list.serviceRatings, n) })}
                      className={`w-8 h-8 rounded-md text-sm border ${list.serviceRatings.includes(n) ? "bg-amber-400 border-amber-400 text-black font-semibold" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}
                    >
                      {n}★
                    </button>
                  ))}
                </div>
                <select
                  value={list.sort}
                  onChange={(e) => patchList({ sort: e.target.value })}
                  className="ml-auto border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white"
                >
                  <option value="NEWEST">Newest first</option>
                  <option value="LOWEST">Lowest rated first</option>
                  <option value="HIGHEST">Highest rated first</option>
                </select>
              </div>

              {(list.followUp === "TO_CALL" || list.followUp === "OPEN_ISSUE") && (
                <p className="text-xs text-gray-500 -mt-2 mb-3">
                  {list.followUp === "TO_CALL" ? "Call-back queue: flagged bookings from the last 30 days, whatever the date range above." : "Every open issue, whatever the date range above."}
                </p>
              )}
              {listError && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm mb-3">{listError}</div>}
              {!reviews.length && !loadingList && !listError && (
                <p className="text-sm text-gray-400 py-8 text-center">No reviews match</p>
              )}
              <div className="space-y-3">
                {reviews.map((r) => (
                  <ReviewCard key={r.feedbackId} r={r} onOpenBooking={openBooking} onFollowUpSaved={onFollowUpSaved} opening={openingBooking} />
                ))}
              </div>
              {loadingList && <p className="text-sm text-gray-400 text-center py-4">Loading…</p>}
              {!loadingList && reviews.length < total && (
                <div className="text-center mt-4">
                  <button
                    type="button"
                    onClick={() => loadPage(reviews.length)}
                    className="px-5 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Load more ({total - reviews.length} left)
                  </button>
                </div>
              )}
            </Card>
          </div>
        </div>
      </Layout>
      {viewingBooking && <OrderDetailsModal booking={viewingBooking} onClose={() => setViewingBooking(null)} />}
    </div>
  );
};

export default CustomerFeedback;
